// Modules
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
// Helpers
import thumbnailPic from '../../../../../../static/images/images_placeholder.svg';
import displayPrice from '../../../../../helpers/displayPrice';
import CellGallery from '../CellGallery/CellGallery';
import CellGalleryWithDotsPagination from '../CellGallery/CellGalleryWithDotsPagination';
import modifyImageLink, { formatImageSrcSet } from '../../../../../utils/modifyImageLink';
import { imageModifierTypes } from '../../../../../constants';
// Styles
import * as styles from './CellPhotoBlock.scss';

const renderPrice = (data) => displayPrice(data, styles);

const renderFavoriteIcon = props => {
  const handleToggleFavorite = (evt) => {
    evt.stopPropagation();
    props.toggleFavorite(evt);
  };

  return !props.showManagePropertiesPack && (
    <span
      onClick={handleToggleFavorite}
      className={cx(
        styles.favoriteIcon,
        'favoriteToggler',
        props.isFavorite ? 'icon-favorite-active-with-border' : 'icon-favorite-disabled'
      )}
    >
        <span className="path1 favoriteToggler" />
        <span className="path2 favoriteToggler" />
    </span>
  );
};

const renderVRIcon = (hasVRLink) => hasVRLink && (
  <span className={cx(styles.labelVR, 'icon', 'icon-vr-view')}>
    <span className="path1" />
    <span className="path2" />
    <span className="path3" />
    <span className="path4" />
    <span className="path5" />
  </span>
);

const renderGallery = (photo, images, videoPoster, isRtl, isLazy, isHomePageSlider,
  withoutRealEstateSlider,
  withoutLoop, withoutPagination, withDotsPagination, onSlideChange, root, id, imageModifier) => {
  if (images && images.length > 1) {
    const GalleryComponent = withDotsPagination ? CellGalleryWithDotsPagination : CellGallery;
    return (
      <GalleryComponent
        onSlideChange={onSlideChange}
        images={images}
        isRtl={isRtl}
        withoutLoop={withoutLoop}
        withoutPagination={withoutPagination}
        isLazy={isLazy}
        withoutRealEstateSlider={withoutRealEstateSlider}
        root={root}
        id={id}
      />
    );
  }

  if (videoPoster) {
    return (
      <img
        alt="video poster"
        property="photo"
        src={videoPoster}
      />
    );
  }

  const modifiedImage = modifyImageLink(photo, imageModifier || imageModifierTypes.GALLERY_THUMBNAIL) || thumbnailPic;
  const modifiedImageMobile = modifyImageLink(photo, imageModifier || imageModifierTypes.GALLERY_THUMBNAIL_MOBILE)
    || thumbnailPic;

  return (
    <img
      alt=""
      property="photo"
      srcSet={formatImageSrcSet([
        { imageUrl: modifiedImage, width: 350 },
        { imageUrl: modifiedImageMobile, width: 600 }
      ])}
      sizes="(max-width: 767px) 600px, (min-width: 768px) 350px"
      src={encodeURI(modifiedImage)}
    />
  );
};

const renderStatusIcon = (active) => (
  <div className={styles.statusCell}>
    <i className={`icon icon-${active ? 'active' : 'inactive'}`}><i className="path1" /><i className="path2" /></i>
  </div>
);

const CellPhotoBlock = (props) => (
  <div
    className={cx(
      styles.propertyCellPhotoBlock,
      props.classNameProperty,
      props.additionalClassName ? styles[props.additionalClassName] : null,
      styles[props.className],
    )}
    ref={props.innerRef || (() => {})}
  >
    {props.showManagePropertiesPack && renderStatusIcon(props.active)}
    {renderVRIcon(props.hasVRLink)}
    {!props.withoutFavorite && !props.isMyProject && renderFavoriteIcon(props) }
    {(props.videoStatusBadge || props.imageStatusBadge) && (
      <div className={styles.mediaStatusBadge}>{props.videoStatusBadge || props.imageStatusBadge}</div>
    )}
    {renderGallery(
      props.photo,
      props.images,
      props.videoPoster,
      props.isRtl,
      props.isLazy,
      props.isHomePageSlider,
      props.withoutRealEstateSlider,
      props.withoutLoop,
      props.withoutPagination,
      props.withDotsPagination,
      props.onSlideChange,
      props.root,
      props.id,
      props.imageModifier
      )}
    {!props.withoutPrice && renderPrice(props)}
  </div>
);

CellPhotoBlock.propTypes = {
  innerRef: PropTypes.func,
  locale: PropTypes.string,
  photo: PropTypes.string,
  images: PropTypes.array,
  videoPoster: PropTypes.string,
  videoStatusBadge: PropTypes.node,
  imageStatusBadge: PropTypes.node,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRtl: PropTypes.bool,
  showManagePropertiesPack: PropTypes.bool,
  toggleFavorite: PropTypes.func,
  isFavorite: PropTypes.bool,
  classNameProperty: PropTypes.string,
  additionalClassName: PropTypes.string,
  hasVRLink: PropTypes.bool,
  active: PropTypes.bool,
  className: PropTypes.string,
  isDynamic: PropTypes.bool,
  isLazy: PropTypes.bool,
  isHomePageSlider: PropTypes.bool,
  withoutRealEstateSlider: PropTypes.bool,
  withoutPrice: PropTypes.bool,
  isMyProject: PropTypes.bool,
  withoutFavorite: PropTypes.bool,
  withoutCurrency: PropTypes.bool,
  withoutLoop: PropTypes.bool,
  withoutPagination: PropTypes.bool,
  currency: PropTypes.object,
  withDotsPagination: PropTypes.bool,
  priceClassName: PropTypes.string,
  onSlideChange: PropTypes.func,
  root: PropTypes.string,
  id: PropTypes.string,
  imageModifier: PropTypes.string
};

export default CellPhotoBlock;
